import React, {useEffect, useState} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {formatBlogDate} from "../../utils/blogs";

export default function BlogDetailModal({
  isDark,
  post,
  onAddComment,
  onClose,
  onToggleLike,
  isSubmitting
}) {
  const [commentAuthor, setCommentAuthor] = useState("");
  const [commentText, setCommentText] = useState("");

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    setCommentAuthor("");
    setCommentText("");
  }, [post]);

  if (!post) {
    return null;
  }

  return (
    <div className="blog-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className={isDark ? "blog-modal blog-modal-dark" : "blog-modal"}
        onClick={event => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="blog-modal-title"
      >
        <button className="blog-modal-close" onClick={onClose} type="button">
          Close
        </button>

        {post.coverImage ? (
          <div className="blog-modal-cover">
            <img src={post.coverImage} alt={post.title} />
          </div>
        ) : null}

        <div className="blog-modal-body">
          <div className="blog-modal-meta-row">
            <span className="blog-pill">{post.source === "medium" ? "Medium" : "Portfolio note"}</span>
            <span className="blog-meta-copy">{formatBlogDate(post.updatedAt || post.createdAt)}</span>
            <span className="blog-meta-copy">{post.readingTime} min read</span>
            <span className="blog-meta-copy">{post.likesCount || 0} likes</span>
            <span className="blog-meta-copy">{post.commentCount || 0} comments</span>
          </div>

          <h2 id="blog-modal-title" className="blog-modal-title">
            {post.title}
          </h2>

          {post.tags && post.tags.length > 0 ? (
            <div className="blog-tag-row blog-tag-row-detail">
              {post.tags.map(tag => (
                <span className="blog-tag" key={tag}>
                  {tag}
                </span>
              ))}
            </div>
          ) : null}

          <div className="blog-markdown-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content || post.excerpt || ""}
            </ReactMarkdown>
          </div>

          {post.source === "medium" && post.externalUrl ? (
            <a className="blog-detail-link" href={post.externalUrl} target="_blank" rel="noreferrer">
              Continue on Medium
            </a>
          ) : null}

          <section className="blog-feedback-panel">
            <div className="blog-feedback-header">
              <h3>Responses</h3>
              <p>Likes and comments are stored in MongoDB and update the public feed.</p>
            </div>

            <div className="blog-local-actions">
              <button className="blog-like-button" disabled={isSubmitting} onClick={() => onToggleLike(post.id)} type="button">
                Like this post
              </button>
            </div>

            <form
              className="blog-comment-form"
              onSubmit={event => {
                event.preventDefault();
                if (!commentText.trim()) {
                  return;
                }

                onAddComment(post.id, {
                  author: commentAuthor.trim() || "Anonymous",
                  text: commentText.trim()
                });
                setCommentText("");
              }}
            >
              <input
                className="blog-input"
                type="text"
                placeholder="Your name"
                value={commentAuthor}
                onChange={event => setCommentAuthor(event.target.value)}
              />
              <textarea
                className="blog-textarea blog-comment-input"
                rows="4"
                placeholder="Leave a note"
                value={commentText}
                onChange={event => setCommentText(event.target.value)}
              />
              <button className="blog-editor-button" disabled={isSubmitting} type="submit">
                Add comment
              </button>
            </form>

            <div className="blog-comment-list">
              {(post.comments || []).length > 0 ? (
                post.comments.map(comment => (
                  <article className="blog-comment-card" key={comment.id}>
                    <div className="blog-comment-header">
                      <strong>{comment.author}</strong>
                      <span>{formatBlogDate(comment.createdAt)}</span>
                    </div>
                    <p>{comment.text}</p>
                  </article>
                ))
              ) : (
                <div className="blog-empty-feedback">No comments yet. Start the conversation.</div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}